// core/stores/hydrationStore.ts — has each app-wide dataset loaded yet?
//
// Notifications, conversations and bookmarks are fetched once per sign-in by the shell
// (useGlobalHydration) because the topbar badges need them on every route. Their pages
// read this status to tell "still loading" and "could not load" apart from "you have
// none", which an empty list alone cannot say.
import { create } from 'zustand';

export type HydratedResource = 'notifications' | 'conversations' | 'bookmarks';
export type HydrationStatus = 'idle' | 'loading' | 'ready' | 'error';

interface HydrationState {
  status: Record<HydratedResource, HydrationStatus>;
  setStatus: (resource: HydratedResource, status: HydrationStatus) => void;
  /** Back to "nothing loaded" (sign-out, session ended). */
  reset: () => void;
}

const IDLE: Record<HydratedResource, HydrationStatus> = {
  notifications: 'idle',
  conversations: 'idle',
  bookmarks: 'idle',
};

export const useHydrationStore = create<HydrationState>((set) => ({
  // Under the test runner the stores boot from the seed (see bootSeed.ts), so the
  // matching status is "ready"; in the app nothing has loaded yet.
  status:
    import.meta.env.MODE === 'test'
      ? { notifications: 'ready', conversations: 'ready', bookmarks: 'ready' }
      : { ...IDLE },
  setStatus: (resource, status) => set((s) => ({ status: { ...s.status, [resource]: status } })),
  reset: () => set({ status: { ...IDLE } }),
}));
